import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useApp } from '../AppContext';

type AuthMode = 'signin' | 'signup' | 'forgot' | 'magic';

// Helper function to translate Firebase error codes to user-friendly messages
const getFirebaseErrorMessage = (error: any): string => {
  const errorCode = error?.code || '';

  switch (errorCode) {
    case 'auth/invalid-credential':
    case 'auth/wrong-password':
    case 'auth/user-not-found':
      return 'Invalid email or password. Please check your credentials and try again.';

    case 'auth/email-already-in-use':
      return 'This email is already registered. Please sign in instead or use a different email.';

    case 'auth/weak-password':
      return 'Password is too weak. Please use at least 6 characters.';

    case 'auth/invalid-email':
      return 'Invalid email address. Please enter a valid email.';

    case 'auth/user-disabled':
      return 'This account has been disabled. Please contact support.';

    case 'auth/too-many-requests':
      return 'Too many failed attempts. Please wait a few minutes and try again.';

    case 'auth/network-request-failed':
      return 'Network error. Please check your internet connection and try again.';

    default:
      // Return a cleaned up error message or a generic one
      const message = error?.message || '';
      if (message.includes('Firebase:')) {
        return 'Authentication failed. Please check your credentials and try again.';
      }
      return message || 'An unexpected error occurred. Please try again.';
  }
};

const Auth: React.FC = () => {
  const navigate = useNavigate();
  const { signIn, signUp, resetPassword, sendMagicLink } = useApp();

  // Get tool from URL params
  const searchParams = new URLSearchParams(window.location.search);
  const tool = searchParams.get('tool') || 'mmt';

  const [mode, setMode] = useState<AuthMode>('signup');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setLoading(true);

    try {
      if (mode === 'signin') {
        await signIn(email, password);
        // Redirect based on tool selection
        if (tool === 'side-hustle') {
          navigate('/side-hustle/home');
        } else {
          navigate('/mmt/landing');
        }
      } else if (mode === 'signup') {
        if (!firstName || !lastName) {
          setError('Please provide your first and last name');
          setLoading(false);
          return;
        }
        await signUp(email, password, firstName, lastName);
        // Redirect based on tool selection
        if (tool === 'side-hustle') {
          navigate('/side-hustle/home');
        } else {
          navigate('/mmt/landing');
        }
      } else if (mode === 'forgot') {
        await resetPassword(email);
        setSuccess('Password reset email sent! Check your inbox.');
      } else if (mode === 'magic') {
        // Store which tool they're accessing for magic link redirect
        window.localStorage.setItem('selectedTool', tool);
        await sendMagicLink(email);
        setSuccess('Magic link sent! Check your email to sign in.');
      }
    } catch (err: any) {
      const friendlyMessage = getFirebaseErrorMessage(err);
      setError(friendlyMessage);
      console.error('Auth error:', err); // Log for debugging
    } finally {
      setLoading(false);
    }
  };

  // Get tool display name
  const getToolName = () => {
    if (tool === 'side-hustle') return 'Side Hustle Builder';
    if (tool === 'mmt') return 'Money Mindset Tool';
    return 'MiniMoney Tools';
  };

  return (
    <div className="min-h-screen bg-secondary flex flex-col items-center justify-center p-6">
      <div className="max-w-md w-full bg-white rounded-3xl p-8 shadow-xl">
        {/* Back Button */}
        <button
          onClick={() => navigate('/')}
          className="flex items-center gap-2 text-gray-600 hover:text-primary transition-colors mb-6"
        >
          <span className="text-xl">←</span>
          <span className="font-semibold">Back to Tools</span>
        </button>

        <h1 className="text-4xl font-bold text-center mb-2 text-primary">
          {getToolName()}
        </h1>
        <p className="text-center text-gray-600 mb-8">
          {mode === 'signin' && 'Welcome back! Sign in to continue'}
          {mode === 'signup' && 'Create your account to get started'}
          {mode === 'forgot' && 'Reset your password'}
          {mode === 'magic' && 'Sign in with a magic link'}
        </p>

        {error && (
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded-lg mb-4">
            {error}
          </div>
        )}

        {success && (
          <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded-lg mb-4">
            {success}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          {mode === 'signup' && (
            <>
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  First Name
                </label>
                <input
                  type="text"
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Last Name
                </label>
                <input
                  type="text"
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary"
                  required
                />
              </div>
            </>
          )}

          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Email
            </label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary"
              required
            />
          </div>

          {mode !== 'forgot' && mode !== 'magic' && (
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Password
              </label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary"
                required
                minLength={6}
              />
            </div>
          )}

          <button
            type="submit"
            disabled={loading}
            className={`w-full py-4 rounded-full text-lg font-semibold transition-all ${
              loading
                ? 'bg-gray-400 cursor-not-allowed'
                : 'bg-primary text-white hover:bg-primary/90 transform hover:scale-105'
            }`}
          >
            {loading ? 'Processing...' : (
              mode === 'signin' ? 'Sign In' :
              mode === 'signup' ? 'Create Account' :
              mode === 'forgot' ? 'Send Reset Email' :
              'Send Magic Link'
            )}
          </button>
        </form>

        <div className="mt-6 space-y-3">
          {mode === 'signin' && (
            <>
              <button
                onClick={() => setMode('forgot')}
                className="w-full text-sm text-primary hover:underline"
              >
                Forgot password?
              </button>
              <button
                onClick={() => setMode('magic')}
                className="w-full text-sm text-primary hover:underline"
              >
                Sign in with magic link instead
              </button>
              <div className="text-center text-sm text-gray-600">
                Don't have an account?{' '}
                <button
                  onClick={() => setMode('signup')}
                  className="text-primary font-semibold hover:underline"
                >
                  Create one
                </button>
              </div>
            </>
          )}

          {mode === 'signup' && (
            <div className="text-center text-sm text-gray-600">
              Already have an account?{' '}
              <button
                onClick={() => setMode('signin')}
                className="text-primary font-semibold hover:underline"
              >
                Sign in
              </button>
            </div>
          )}

          {(mode === 'forgot' || mode === 'magic') && (
            <div className="text-center">
              <button
                onClick={() => setMode('signin')}
                className="text-sm text-primary hover:underline"
              >
                ← Back to sign in
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Auth;
